import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { Button } from '@/ui/base/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';

import type { ColaboradorDoCredenciado, PerfilDeAcessoDoColaborador } from './contrato';

const esquema = z.object({
  nome: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(200),
  email: z.string().trim().min(1, 'E-mail obrigatório').email('E-mail inválido'),
  cargo: z.string().trim().max(120),
  telefone: z.string().trim().max(20),
  perfil: z.enum(['administrador', 'financeiro', 'operador']),
  ativo: z.boolean(),
});

type ValoresDoFormulario = z.infer<typeof esquema>;

const ROTULO_DO_PERFIL: Record<PerfilDeAcessoDoColaborador, string> = {
  administrador: 'Administrador',
  financeiro: 'Financeiro',
  operador: 'Operador',
};

const DESCRICAO_DO_PERFIL: Record<PerfilDeAcessoDoColaborador, string> = {
  administrador: 'Acesso completo, inclusive à gestão da equipe.',
  financeiro: 'Acesso a cobranças, remuneração e extratos.',
  operador: 'Acesso à operação: veículos, condutores e ordens de serviço.',
};

export interface EquipeFormularioProps {
  /** Presente ao editar um colaborador existente; ausente ao cadastrar um novo. */
  colaborador?: ColaboradorDoCredenciado;
  onCancelar: () => void;
}

/**
 * Formulário de adicionar e editar colaborador. O "Salvar" valida os campos e
 * só mostra o aviso de cadastro indisponível: sem API de equipe, gravar seria
 * fingir (CLAUDE.md, seção "Proibido"). O formulário fica aberto com o que foi
 * digitado, como em `operacao/condutores/CondutorFormulario.tsx`.
 */
export function EquipeFormulario({ colaborador, onCancelar }: EquipeFormularioProps) {
  const form = useForm<ValoresDoFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      nome: colaborador?.nome ?? '',
      email: colaborador?.email ?? '',
      cargo: colaborador?.cargo ?? '',
      telefone: colaborador?.telefone ?? '',
      perfil: colaborador?.perfil ?? 'operador',
      ativo: colaborador?.ativo ?? true,
    },
    mode: 'onSubmit',
  });

  function enviar() {
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(enviar)} className="space-y-5" noValidate>
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome completo</FormLabel>
              <FormControl>
                <Input {...field} maxLength={200} autoComplete="off" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input {...field} type="email" inputMode="email" autoComplete="off" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="cargo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cargo</FormLabel>
                <FormControl>
                  <Input {...field} maxLength={120} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" inputMode="tel" maxLength={20} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="perfil"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Perfil de acesso</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(Object.keys(ROTULO_DO_PERFIL) as PerfilDeAcessoDoColaborador[]).map(
                    (perfil) => (
                      <SelectItem key={perfil} value={perfil}>
                        <div className="flex flex-col">
                          <span className="font-medium">{ROTULO_DO_PERFIL[perfil]}</span>
                          <span className="text-xs text-muted-foreground">
                            {DESCRICAO_DO_PERFIL[perfil]}
                          </span>
                        </div>
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between gap-4 rounded-xl border border-input p-4">
              <div>
                <FormLabel className="text-sm font-medium normal-case tracking-normal text-foreground">
                  Acesso ativo
                </FormLabel>
                <p className="text-xs text-muted-foreground">
                  Quando desativado, o colaborador não consegue mais entrar no portal.
                </p>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit">Salvar</Button>
        </div>
      </form>
    </Form>
  );
}

export default EquipeFormulario;
